<template>
  <div class="frame-common" :class="getMainFrameClasses">
    <header class="frame-common__header zoom-70">
      <div class="frame-common__header__title">
        <router-link :to="{ name: 'index' }" class="frame-common__header__title__link">
          <div class="frame-common__header__title__link__logo">
            <SemmelweisLogoSvg />
          </div>
          <div class="title">
            <div class="frame-common__header__title__link__main">
              {{ $t("header.projectTitle") }}
            </div>
            <div class="frame-common__header__title__link__sub">
              {{ $t("header.title") }}
            </div>
          </div>
        </router-link>
      </div>
      <div v-if="$user.isLoggedIn()" class="frame-common__header__user">
        <div class="frame-common__header__user__info">
          <div class="frame-common__header__user__info__name">{{ $user.data.name }}</div>
          <div class="frame-common__header__user__info__email">
            {{ $user.data.email }}
          </div>
          <div class="frame-common__header__user__info__entitlement">
            <span v-if="$user.data.roles && $user.data.roles.length > 0">
              {{ $user.data.roles.join(", ") }}
            </span>
            <span v-else> Nincs jogosultsága </span>
          </div>
        </div>
        <div
          class="frame-common__header__user__date position-absolute top-0 end-0"
          :class="$inactivityObserver.running.value ? ' px-1 py-2 m-1' : ' px-4 py-2 m-2'"
        >
          <template v-if="$inactivityObserver.running.value">
            Automatikus kiléptetésig hátralévő idő ({{
              $inactivityObserver.expireAt.formatted
            }})
          </template>

          <template v-else> Automatikus kiléptetés: kikapcsolva </template>
        </div>
        <div class="frame-common__header__user__avatar">
          <AvatarSvg />
        </div>
        <LanguageSelector class="frame-common__header__user__language" />
        <div class="frame-common__header__user__logout">
          <router-link
            v-if="$user.isLoggedIn()"
            :to="{ name: 'logout' }"
            :title="$t('navigation.logout')"
          />
        </div>
      </div>
    </header>

    <div class="zoom-70">
      <div>
        <Navigation />
        <div v-if="!isGuest" class="d-flex">
          <!--
ALMENÜ / GYORS GOMBOK A FŐMENÜ ALÁ

Ezek a gombok nem teljes értékű főmenüpontok, hanem szerepkörhöz kötött
gyors elérések. Céljuk, hogy a felhasználó egy kattintással elérje a saját
munkafolyamatához tartozó legfontosabb funkciókat.

Példák:
- ügylista / munkalista gyors megnyitása
- cég érintettség keresés indítása
- hivatalból indított ügy létrehozása
- később akár szűrt lista vagy konkrét ügy / azonosító alapú nézet megnyitása

A megjelenítés szerepkör alapján történik.
A szerepkörök Laravel oldalról érkeznek ($user.data.roles).
-->

          <!-- Adminisztrátor -->
          <div v-if="userHasRole('Adminisztrátor')" class="d-flex"></div>

          <!-- Ellenőr -->
          <div v-if="userHasRole('Ellenőr')" class="d-flex"></div>

          <!-- JIF admin -->
          <div v-if="userHasRole('JIF admin')" class="d-flex">
            <!-- Egyelőre ennél a két funkciónál marad a demo állapot,
     jelenleg még nincs rájuk aktív üzleti igény. -->
            <!--
<JifAdminWorklistButton />
<JifAdminCompanysearchButton />
-->
            <template v-if="isJifAdminParameterSection">
              <JifAdminEmailMasterData />
              <JifAdminSelectTextManagement />
              <JifAdminEmailNotificationRules />
            </template>
          </div>

          <template v-if="isJifAdminUserManagementSection">
            <JifAdminLdapUserSearchButton />
            <JifAdminDatabaseUserSearchButton />
          </template>

          <!-- JIF szakértő -->
          <div v-if="userHasRole('JIF szakértő')" class="d-flex"></div>

          <!-- Főigazgató -->
          <div v-if="userHasRole('Főigazgató')" class="d-flex">
            <!-- Egyelőre nincs szükség almenü / gyorsgomb megjelenítésére ennél a szerepkörnél. -->
            <!-- <DirectorWorklistButton /> -->
          </div>
        </div>
      </div>

      <router-view
        style=""
        class="frame-common__content__view"
        v-if="!$user.isLoggedIn()"
        v-slot="{ Component }"
      >
        <Transition>
          <component :is="Component" :class="getContentViewClasses" />
        </Transition>
      </router-view>
      <router-view
        class="frame-common__content__view"
        v-else
        :class="getContentViewClasses"
      ></router-view>
      <!-- 
  MEGJEGYZÉS:
  Az alábbi lábléc Bootstrap❗-alapú elrendezést használ.
  A struktúra és a stílus kialakítása Bootstrap grid és utility 
  osztályokra épül, ezért a kompatibilitás érdekében a layout
  Bootstrap környezetben került megvalósításra.
-->
      <footer>
        <Footer />
      </footer>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/Navigation.vue";

import SemmelweisLogoSvg from "@media/images/vector/sote-logo.svg";
import AvatarSvg from "@media/images/vector/avatar.svg";
import Footer from "@/components/Footer.vue";

// ======================================================================
// JIF admin – MENÜGOMBOK
// (Role: "JIF admin")
// ======================================================================
import JifAdminCompanysearchButton from "../authenticated/dashboard/jifadmin/navigation-pages-buttons/companysearchbutton.vue";
import JifAdminWorklistButton from "../authenticated/dashboard/jifadmin/navigation-pages-buttons/worklistbutton.vue";

import JifAdminEmailMasterData from "../authenticated/dashboard/jifadmin/navigation-pages-buttons/emailmasterdatabutton.vue";
import JifAdminSelectTextManagement from "../authenticated/dashboard/jifadmin/navigation-pages-buttons/selecttextmanagementbutton.vue";
import JifAdminEmailNotificationRules from "../authenticated/dashboard/jifadmin/navigation-pages-buttons/emailnotificationrulesbutton.vue";

import JifAdminLdapUserSearchButton from "../authenticated/dashboard/jifadmin/navigation-pages-buttons/userldapsearchbutton.vue";
import JifAdminDatabaseUserSearchButton from "../authenticated/dashboard/jifadmin/navigation-pages-buttons/userdatabasesearchbutton.vue";
// ======================================================================
// Főigazgató – MENÜGOMBOK
// (Role: "Főigazgató")
// ======================================================================
import DirectorWorklistButton from "../authenticated/dashboard/director/navigation-pages-buttons/worklistbutton.vue";

// Language selector component
// Példa implementáció a nyelvváltó működésének bemutatására.
// A selector segítségével a projektben lévő szövegek dinamikusan változnak a kiválasztott nyelv alapján.
import LanguageSelector from "../../components/LanguageSelector.vue";

export default {
  components: {
    Footer,
    Navigation,
    SemmelweisLogoSvg,
    AvatarSvg,
    JifAdminCompanysearchButton,
    JifAdminWorklistButton,
    DirectorWorklistButton,
    LanguageSelector,
    JifAdminEmailMasterData,
    JifAdminEmailNotificationRules,
    JifAdminSelectTextManagement,
    JifAdminLdapUserSearchButton,
    JifAdminDatabaseUserSearchButton,
  },

  i18n: {
    messages: {
      en: {
        header: {
          title: "Semmelweis University",
          projectTitle: "Claritas Application",
        },
      },
      hu: {
        header: {
          title: "Semmelweis Egyetem",
          projectTitle: "Claritas alkalmazás",
        },
      },
      de: {
        header: {
          title: "Semmelweis Universität",
          projectTitle: "Claritas Anwendung",
        },
      },
    },
  },

  data() {
    return {
      userReady: false,
    };
  },

  beforeMount() {
    if (this.$user?.data?.roles?.length > 0) {
      // console.log("Felhasználó szerepe:", this.$user.data.roles[0].name);
      this.userReady = true;
    } else if (this.$user && !this.$user.data) {
      // console.log("Felhasználó nincs bejelentkezve.");
    } else {
      // console.log("Nincs jogosultság vagy nem betöltött adatok.");
    }
  },

  mounted() {
    if (this.userReady) {
      // console.log("A felhasználói adatok betöltve, a menü megjelenhet.");
    }
  },

  methods: {
    getThemeName() {
      // Or attach a select menu for the theming and return the proper value from that.
      return "-styling-theme-default";
    },

    userHasRole(roleName) {
      return this.$user?.data?.roles?.includes(roleName) ?? false;
    },
  },

  computed: {
    // Megmondja, hogy a felhasználó a JIF admin Paraméter-kezelés szekciójában van-e.
    //
    // Erre azért van szükség, mert az "Email törzsadatok" és a "Választható szövegek"
    // nem külön főmenüpontként jelennek meg, hanem a Paraméter-kezelés alatti
    // gyorsgomb / almenü funkcióként.
    //
    // Így ezek a gombok csak akkor látszanak, ha a felhasználó:
    // - a Paraméter-kezelés oldalon van,
    // - vagy annak valamelyik kapcsolódó aloldalán.
    isJifAdminParameterSection() {
      return [
        "jifadmin.parametermanagement",
        "jifadmin.emailmasterdata",
        "jifadmin.emailnotificationrules",
        "jifadmin.selecttextmanagement",
      ].includes(this.$route.name);
    },

    // Megmondja, hogy a felhasználó a JIF admin Felhasználó-kezelés szekciójában van-e.
    //
    // Erre azért van szükség, mert az LDAP felhasználókeresés és a
    // Helyi felhasználók kezelése nem külön főmenüpontként jelenik meg,
    // hanem a Felhasználó-kezelés logikai csoportjához tartozik.
    //
    // Így a kapcsolódó gyorsgombok csak akkor jelennek meg,
    // ha a felhasználó:
    // - a Felhasználó-kezelés oldalon van,
    // - vagy annak valamelyik kapcsolódó aloldalán.
    isJifAdminUserManagementSection() {
      return [
        "jifadmin.usermanagement",
        "jifadmin.userldapsearch",
        "jifadmin.userdatabasesearch",
      ].includes(this.$route.name);
    },

    getMainFrameClasses() {
      return [this.$user.isLoggedIn() ? "-user" : "-guest", this.getThemeName()];
    },

    getContentViewClasses() {
      return this.getMainFrameClasses;
    },

    isGuest() {
      return this.$route.name === "login";
    },
  },
};
</script>

<style lang="scss">
@import "@scss/pages/common/Frame.scss";

.frame-common {
  user-select: none;
}

.title {
  display: inline-block;
  padding-top: 15px;
  width: auto;
}

/* ==========================================================================
   FEJLESZTŐI MEGJEGYZÉS – FRONTEND MÉRETEZÉSI DÖNTÉS - Takács Bálint
   ==========================================================================

   Az Alkalmazásfejlesztési Osztály jelenlegi kapacitásai nem teszik lehetővé
   egy olyan részletes, többkörös frontend-finomhangolási folyamat megvalósítását,
   amely a különböző kijelző- és felbontásméretekhez teljes mértékben igazodó,
   egységes megjelenítést garantálna.

   A felhasználói visszajelzések alapján jól látható, hogy eltérő monitor-
   méreteken különböző igények és elvárások jelennek meg ugyanazzal a felülettel
   kapcsolatban. Mivel azonban nem áll rendelkezésre egységes design specifikáció
   (pl. Figma-tervek), valamint nincs pontos információ arról, hogy a felhasználók
   milyen kijelzőkön dolgoznak, jelenleg nem reális cél minden felbontásra
   külön optimalizálni a megjelenítést.

   Ennek áthidalására egy pragmatikus, köztes megoldás került bevezetésre a részemről:
   a teljes felület – beleértve a fejlécet, menüt, fő tartalmat és láblécet –
   egységesen 70%-os méretarányon jelenik meg.

   Bár ez nem tekinthető végleges vagy tökéletes UX-megoldásnak, a jelenlegi
   erőforrás- és határidőkeretek között stabil, használható kompromisszumot
   biztosít, amely a gyakorlatban minden érintett felhasználónál megfelelőnek
   bizonyult.

   A döntést többek között olyan felhasználói visszajelzések is indokolták, mint:
     - különböző monitorokon eltérő méretűnek és arányúnak tűnő elemek,
     - bizonyos szövegek vagy gombok „túl nagynak” vagy „túl kicsinek” érzése,
     - az a felhasználói elvárás, hogy a design személyre vagy eszközre
       szabottan módosuljon.

   Ezek a kérések azonban célzott design-tervezést, egységes UX-irányelveket,
   több platformra optimalizált prototípusokat és dedikált erőforrást igényelnének.
   Mindez jelenleg nem áll rendelkezésre, így a külön monitor-méretekre történő
   finomhangolás nem lenne reálisan, fenntartható módon megvalósítható.

   Nagyobb szervezetekben (pl. banki környezetben) külön, dedikált UI/UX csapat
   foglalkozik a többféle kijelzőmérethez igazodó design kialakításával. A
   Semmelweis Egyetemen jelenleg ilyen erőforrás nem❗áll rendelkezésre, ezért
   a fejlesztés elsődleges prioritása a rendszer megbízható működése, illetve
   a gyors és eredményes felhasználás támogatása.

   A 70%-os méretezés tehát tudatos, gyakorlati kompromisszum a
   használhatóság, a rendelkezésre álló erőforrások és a rövid fejlesztési
   határidők figyelembevételével.

    Ahogy az osztályvezető, Zrínyi Miklós is fogalmazott:
   „nem kell túlgondolni a kulcsint — a lényeg, hogy működjön.”
   ========================================================================== */

/* 
==============================================================================
💡 Jövőbeni fejlesztési lehetőség – Zoom eltávolítása (70%)
==============================================================================

Amennyiben a későbbiekben elegendő idő, kapacitás és design-támogatás áll 
rendelkezésre, a jelenleg használt 70%-os méretezés végleg eltávolítható, 
és a felület visszaállítható az alapértelmezett arányokra. 
Ezt követően lehetőség nyílik egy egységes, minden kijelzőméretre 
optimalizált UX-design kialakítására.

🎯 Célok
--------------------------------
• A 70%-os frontend zoom teljes eltávolítása  
• A layout visszaállítása normál méretre  
• Reszponzív, kijelzőfüggetlen megjelenés biztosítása  
• A footer továbbra is külön blokkban maradjon  


1️⃣ `.zoom-70` class eltávolítása SCSS-ből
--------------------------------
Ahol jelenleg ez szerepel:

.zoom-70 {
  zoom: 70%;
}

👉 Teljes egészében törölni kell
– sem a class, sem a CSS ne maradjon meg


3️⃣ Footer külön szekcióban
--------------------------------
A template lezárása így nézzen ki:

</router-view>
</div>
<footer>
  <Footer />
</footer>
</div>
</template>


4️⃣ Frame.scss módosítás
--------------------------------
Fájl:
resources/scss/pages/common/Frame.scss

Keresd meg az alábbi részt:

&__view {
  height: 100%;

👉 A `height: 100%` sort törölni kell - (background-color: #F8FAFF;)

✔ Eredmény
--------------------------------
• A felület természetes, böngésző-alapú méretben jelenik meg
*/

.zoom-70 {
  zoom: 70%;
}

/**
 * F5 / login / logout közbeni "felugrás" bugfix:
 * A .frame-common grid 2. sora (1fr) a .zoom-70 wrapper. Ha a router-view egy pillanatra üres,
 * a wrapper magassága össze tud esni → a footer felcsúszik.
 *
 * Ezzel flex oszloppá tesszük a wrappert, és a footert margin-top:auto-val mindig alul "lenyomjuk",
 * így nem ugrál a layout a gyors route váltásoknál.
 */

.frame-common > .zoom-70 {
  min-height: 0; // grid bugfix
  height: 100%; // töltse a 1fr sort
  display: flex;
  flex-direction: column;
}
.frame-common > .zoom-70 > footer {
  margin-top: auto; // mindig alul maradjon a wrapperen belül
}
</style>
